// Pure state-transition logic for ListingsScroller.goBack — extracted so it
// can be unit-tested without mounting the full component tree.
//
// Returns the partial state object that the component's setState call expects.
// All side effects (scroll reset, deferred detailsHasRendered clear) stay in
// the component method.

export function computeGoBackState(state) {
	// Fast path — no drilled history, collapse to the top-level listing view.
	if (state.history.length === 0) {
		return {
			tierLevel: 0,
			tieredDetails: [],
			tieredHeaders: [],
			detailsGrp: [],
			detailsHasRendered: false,
			detailsTitle: '',
			history: [],
			detailsPage: false,
			currentDetailAction: state.detailsTouchAction
		};
	}

	const lastHistoryElement = state.history[state.history.length - 1];
	const historyWithoutLastElement = state.history.slice(0, -1);

	// Default fallback — overridden below when restoring from a history entry
	// that carries its own currentDetailAction.
	const newState = {
		history: historyWithoutLastElement,
		currentDetailAction: state.detailsTouchAction
	};

	// Tier+1 navigation — indicated by either:
	//   1. detailsTouchAction === 10 (came from a detail page), or
	//   2. returnMultiple === true (came from a listing row with tier+1).
	if (lastHistoryElement.detailsTouchAction === 10 || lastHistoryElement.returnMultiple === true) {
		if (historyWithoutLastElement.length === 0) {
			// Parent tier was the top-level listing. Restore its
			// currentDetailAction from the saved history entry so the
			// "Select a listing for more details" placeholder re-appears when
			// that tier's Touch Action is Tier +1. (DEV-11645 QA fix)
			newState.currentDetailAction = lastHistoryElement.currentDetailAction ?? state.detailsTouchAction;
			Object.assign(newState, {
				tierLevel: 0,
				tieredDetails: [],
				tieredHeaders: [],
				detailsTitle: '',
				detailsPage: false,
				detailsHasRendered: false
			});
		} else {
			// Tier+1 was drilled from a deeper state; skip the tier+1 artifact
			// and restore the state that preceded it.
			const previousHistoryElement = historyWithoutLastElement[historyWithoutLastElement.length - 1];
			newState.currentDetailAction = previousHistoryElement.currentDetailAction ?? state.detailsTouchAction;

			if (previousHistoryElement.type === 'detail') {
				Object.assign(newState, {
					tierLevel: previousHistoryElement.tierLevel ?? 0,
					tieredDetails: [],
					tieredHeaders: [],
					details: previousHistoryElement.details,
					detailsPage: true,
					detailsUUID: previousHistoryElement.detailsUUID,
					detailsGrp: previousHistoryElement.detailsGrp,
					detailsTouchAction: previousHistoryElement.detailsTouchAction,
					detailsTitle: previousHistoryElement.detailsTitle || state.detailsTitle
				});
				newState.history = historyWithoutLastElement.slice(0, -1);
			} else if (previousHistoryElement.type === 'listing') {
				Object.assign(newState, {
					tierLevel: previousHistoryElement.tierLevel ?? 0,
					tieredDetails: previousHistoryElement.tieredDetails,
					tieredHeaders: previousHistoryElement.tieredHeaders,
					detailsTitle: previousHistoryElement.detailsTitle,
					detailsPage: false,
					details: []
				});
				newState.history = historyWithoutLastElement.slice(0, -1);
			}
		}
	} else if (lastHistoryElement.type === 'detail') {
		Object.assign(newState, {
			details: lastHistoryElement.details,
			detailsPage: true,
			detailsUUID: lastHistoryElement.detailsUUID,
			detailsGrp: lastHistoryElement.detailsGrp,
			detailsTouchAction: lastHistoryElement.detailsTouchAction
		});
	} else if (lastHistoryElement.type === 'listing') {
		newState.currentDetailAction = lastHistoryElement.currentDetailAction ?? state.detailsTouchAction;
		Object.assign(newState, {
			tierLevel: lastHistoryElement.tierLevel ?? 0,
			tieredDetails: lastHistoryElement.tieredDetails,
			tieredHeaders: lastHistoryElement.tieredHeaders,
			detailsTitle: lastHistoryElement.detailsTitle,
			details: [],
			detailsPage: false
		});
	}

	return newState;
}
